"use client";

import { useCallback, useRef, useState } from "react";

import { AssetImage } from "@/components/optilase/asset-image";
import { CarouselDots } from "@/components/optilase/carousel-dots";
import { activeSlide } from "@/components/optilase/carousel-page";
import { DisclosureChevron } from "@/components/optilase/icons";
import { TextLink } from "@/components/optilase/text-link";
import { onSurface, type Surface } from "@/components/optilase/surface";
import { cn } from "@/lib/utils";

export type WhyCard = {
  icon: string;
  iconAlt?: string;
  title: string;
  body: string;
};

export type WhyChooseViewProps = {
  heading: string;
  cards: WhyCard[];
  linkLabel?: string;
  linkHref?: string;
  surface?: Surface | null;
};

export default function WhyChooseView({
  heading,
  cards,
  linkLabel,
  linkHref = "#",
  surface,
}: WhyChooseViewProps) {
  const [open, setOpen] = useState<string | null>(null);
  const scroller = useRef<HTMLUListElement>(null);
  const [page, setPage] = useState(0);

  const sync = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setPage(activeSlide(el));
  }, []);

  return (
    <section
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 md:py-12 lg:px-20 lg:py-20", surface ? "" : "bg-white")}
    >
      <div className="mx-auto max-w-7xl">
        <h2
          className={cn(
            "text-section",
            onSurface(surface, "text-white", "text-navy-03", "text-navy-03"),
          )}
        >
          {heading}
        </h2>

        <ul
          ref={scroller}
          onScroll={sync}
          className="mt-8 flex flex-col gap-2 md:-mx-10 md:snap-x md:snap-mandatory md:scroll-pl-10 md:[scrollbar-width:none] md:flex-row md:overflow-x-auto md:px-10 md:pb-2 lg:mx-0 lg:mt-10 lg:scroll-pl-0 lg:overflow-visible lg:px-0 lg:pb-0 md:[&::-webkit-scrollbar]:hidden"
        >
          {cards.map((c) => {
            const isOpen = open === c.title;
            return (
              <li
                key={c.title}
                className="md:flex md:w-[308px] md:shrink-0 md:snap-start lg:w-auto lg:flex-1"
              >
                <div className="w-full md:hidden">
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : c.title)}
                    aria-expanded={isOpen}
                    className={cn(
                      "bg-navy-03 flex min-h-28.5 w-full cursor-pointer items-center gap-4 px-4 py-2 text-left text-white",
                      isOpen ? "rounded-t-xl" : "rounded-xl",
                    )}
                  >
                    <AssetImage
                      src={c.icon}
                      alt={c.iconAlt || c.title}
                      aria-hidden
                      width={70}
                      height={70}
                      className="h-17.5 w-auto shrink-0"
                    />
                    <span className="text-h6 flex-1">{c.title}</span>
                    <DisclosureChevron
                      className={cn(
                        "text-teal-05 shrink-0 transition-transform",
                        isOpen && "rotate-90",
                      )}
                    />
                  </button>
                  {isOpen ? (
                    <p className="bg-navy-03 rounded-b-xl px-4 pb-4 text-base leading-relaxed tracking-[-0.01em] text-white">
                      {c.body}
                    </p>
                  ) : null}
                </div>

                <div className="bg-navy-03 hidden h-full flex-col items-center gap-3 rounded-xl p-6 text-center text-white md:flex lg:p-8">
                  <AssetImage
                    src={c.icon}
                    alt={c.iconAlt || c.title}
                    aria-hidden
                    width={100}
                    height={100}
                    className="h-17.5 w-auto lg:h-25"
                  />
                  <h3 className="text-h5">{c.title}</h3>
                  <p className="text-base leading-relaxed tracking-[-0.01em]">{c.body}</p>
                </div>
              </li>
            );
          })}
        </ul>

        <CarouselDots
          count={cards.length}
          active={page}
          label="card"
          onSelect={(i) => {
            const el = scroller.current;
            const card = el?.children[i] as HTMLElement | undefined;
            card?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
          }}
          className="hidden md:flex lg:hidden"
        />

        {linkLabel ? (
          <div className="mt-8 flex justify-center lg:mt-10">
            <TextLink
              href={linkHref}
              className={onSurface(surface, "text-white", "text-navy-03", "text-navy-03")}
            >
              {linkLabel}
            </TextLink>
          </div>
        ) : null}
      </div>
    </section>
  );
}
